import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, FileText, Download, AlertCircle, PenTool, Calendar, User, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useIsMounted } from '@/hooks/useSafeState';

const DocumentApprovals = () => {
  const { user, token } = useAuth();
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [approvedDocuments, setApprovedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApproved, setShowApproved] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalData, setApprovalData] = useState({
    signerName: '',
    signatureDate: new Date().toISOString().split('T')[0],
    comments: ''
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Use custom hook to track if component is mounted
  const isMountedRef = useIsMounted();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const abortController = new AbortController();
    isMountedRef.current = true;

    const loadData = async () => {
      if (user?.role === 'admin') {
        try {
          // Fetch pending documents
          const pendingResponse = await axios.get(`${API}/documents/pending-approval`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          });
          if (isMountedRef.current) {
            setPendingDocuments(pendingResponse.data);
          }

          // Fetch approved documents
          const approvedResponse = await axios.get(`${API}/documents?limit=20`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: abortController.signal
          });
          if (isMountedRef.current) {
            const approved = approvedResponse.data
              .filter(doc => doc.approval_status === 'approved' && doc.approved_at)
              .sort((a, b) => new Date(b.approved_at) - new Date(a.approved_at))
              .slice(0, 10);
            setApprovedDocuments(approved);
          }
        } catch (error) {
          if (error.name !== 'CanceledError' && error.name !== 'AbortError' && isMountedRef.current) {
            toast.error('Erreur lors du chargement des documents');
            console.error('Error fetching documents:', error);
          }
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      }
    };

    loadData();

    return () => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, [user, token]);

  const fetchPendingDocuments = async (signal = null) => {
    try {
      const response = await axios.get(`${API}/documents/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` },
        ...(signal && { signal })
      });
      if (isMountedRef.current) {
        setPendingDocuments(response.data);
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        if (isMountedRef.current) {
          toast.error('Erreur lors du chargement des documents');
        }
        console.error('Error fetching pending documents:', error);
      }
    }
  };

  const fetchApprovedDocuments = async (signal = null) => {
    try {
      const response = await axios.get(`${API}/documents?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
        ...(signal && { signal })
      });
      // Filter for recently approved documents
      const approved = response.data
        .filter(doc => doc.approval_status === 'approved' && doc.approved_at)
        .sort((a, b) => new Date(b.approved_at) - new Date(a.approved_at))
        .slice(0, 10);
      if (isMountedRef.current) {
        setApprovedDocuments(approved);
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Error fetching approved documents:', error);
      }
    }
  };

  const handleApproveClick = (document) => {
    setSelectedDocument(document);
    setApprovalData({
      signerName: user?.full_name || '',
      signatureDate: new Date().toISOString().split('T')[0],
      comments: ''
    });
    setApproveDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!approvalData.signerName.trim()) {
      toast.error('Veuillez saisir le nom du signataire');
      return;
    }

    const abortController = new AbortController();
    
    try {
      await axios.put(
        `${API}/documents/${selectedDocument.id}/approve`,
        {
          signer_name: approvalData.signerName,
          signature_date: approvalData.signatureDate,
          comments: approvalData.comments
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );
      
      if (isMountedRef.current) {
        toast.success('✅ Document approuvé avec signature électronique');
        setApproveDialogOpen(false);
        setSelectedDocument(null);
        setApprovalData({
          signerName: '',
          signatureDate: new Date().toISOString().split('T')[0],
          comments: ''
        });
        
        // Clean up preview URL
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }
      
      await fetchPendingDocuments(abortController.signal);
      await fetchApprovedDocuments(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        if (isMountedRef.current) {
          toast.error('Erreur lors de l\'approbation');
        }
        console.error('Error approving document:', error);
      }
    }
  };

  const handleRejectClick = (document) => {
    setSelectedDocument(document);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Veuillez fournir une raison pour le rejet');
      return;
    }

    const abortController = new AbortController();

    try {
      await axios.put(
        `${API}/documents/${selectedDocument.id}/reject?reason=${encodeURIComponent(rejectionReason)}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal
        }
      );
      toast.success('Document rejeté');
      setRejectDialogOpen(false);
      setSelectedDocument(null);
      setRejectionReason('');
      await fetchPendingDocuments(abortController.signal);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        toast.error('Erreur lors du rejet');
        console.error('Error rejecting document:', error);
      }
    }
  };

  const handleDownload = async (documentId, documentName) => {
    const abortController = new AbortController();
    
    try {
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        signal: abortController.signal
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up blob URL
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        toast.error('Erreur lors du téléchargement');
        console.error('Error downloading document:', error);
      }
    }
  };

  const handlePreview = async (documentId) => {
    try {
      // Clean up previous preview URL
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
      console.error('Error previewing document:', error);
    }
  };

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const getDocumentTypeLabel = (docType) => {
    const types = {
      'contrat_initial': 'Contrat Initial',
      'contrat_service': 'Contrat de Service',
      'recu': 'Reçu',
    };
    return types[docType] || docType;
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'administratif': 'Administratif',
      'financier': 'Financier',
      'autres': 'Autres'
    };
    return categories[category] || category;
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-600" />
            <p className="text-lg text-gray-600">Accès réservé aux administrateurs</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Approbation de Documents
            </h1>
            <p className="text-gray-600 mt-2">
              Gérez les documents en attente d'approbation
            </p>
          </div>
          <Badge className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-4 py-2 text-lg">
            <Clock className="h-5 w-5 mr-2" />
            {pendingDocuments.length} en attente
          </Badge>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <FileText className="h-6 w-6 text-red-600" />
              Documents en Attente d'Approbation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingDocuments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 opacity-50 text-green-600" />
                <p className="text-lg">Aucun document en attente</p>
                <p className="text-sm mt-2">Tous les documents ont été traités</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingDocuments.map((doc) => (
                  <Card key={doc.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <FileText className="h-6 w-6 text-red-600" />
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">
                                {doc.name || doc.filename}
                              </h3>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {getDocumentTypeLabel(doc.contract_type || doc.document_type)}
                                </Badge>
                                <Badge variant="outline" className="text-xs bg-blue-50">
                                  {getCategoryLabel(doc.category)}
                                </Badge>
                                <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  En attente
                                </Badge>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                            <div>
                              <p className="text-gray-600">
                                <span className="font-semibold">Client :</span> {doc.client_name}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-semibold">Dossier :</span> {doc.file_number}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">
                                <span className="font-semibold">Taille :</span> {(doc.size / 1024).toFixed(2)} KB
                              </p>
                              <p className="text-gray-600">
                                <span className="font-semibold">Date de création :</span>{' '}
                                {doc.uploaded_at ? format(new Date(doc.uploaded_at), 'dd/MM/yyyy HH:mm') : 'N/A'}
                              </p>
                            </div>
                          </div>

                          {doc.quote_id && (
                            <div className="mt-3">
                              <Badge variant="outline" className="text-xs">
                                <FileText className="h-3 w-3 mr-1" />
                                Généré depuis devis
                              </Badge>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePreview(doc.id)}
                            className="w-full"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Prévisualiser
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(doc.id, doc.name || doc.filename)}
                            className="w-full"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Télécharger
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveClick(doc)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                          >
                            <PenTool className="h-4 w-4 mr-2" />
                            Signer & Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectClick(doc)}
                            className="w-full"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Rejeter
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Approved Documents History */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                Historique des Approbations Récentes
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowApproved(!showApproved)}
              >
                {showApproved ? 'Masquer' : 'Afficher'} ({approvedDocuments.length})
              </Button>
            </div>
          </CardHeader>
          {showApproved && (
            <CardContent>
              {approvedDocuments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucun document approuvé récemment</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvedDocuments.map((doc) => (
                    <Card key={doc.id} className="border border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {doc.name || doc.filename}
                                </h4>
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                  {doc.signer_name && (
                                    <span className="flex items-center gap-1">
                                      <PenTool className="h-3 w-3" />
                                      Signé par: <strong>{doc.signer_name}</strong>
                                    </span>
                                  )}
                                  {doc.signature_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      Date: {format(new Date(doc.signature_date), 'dd/MM/yyyy', { locale: fr })}
                                    </span>
                                  )}
                                  {doc.approved_at && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      Approuvé le: {format(new Date(doc.approved_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                                    </span>
                                  )}
                                </div>
                                {doc.approval_comments && (
                                  <p className="mt-2 text-sm text-gray-600 italic">
                                    "{doc.approval_comments}"
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <Badge className="bg-green-600 text-white">
                            Approuvé
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>

      {/* Approve Dialog with Electronic Signature */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <PenTool className="h-6 w-6 text-green-600" />
              Signature Électronique & Approbation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Document Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-blue-600 mt-1" />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {selectedDocument?.name || selectedDocument?.filename}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                      <span>Client : {selectedDocument?.client_name}</span>
                      <span>•</span>
                      <span>Dossier : {selectedDocument?.file_number}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Section */}
            {previewUrl && (
              <Card className="border-2 border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-700">
                    Prévisualisation du Document
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <iframe
                    src={previewUrl}
                    className="w-full h-96 border-0"
                    title="Document Preview"
                  />
                </CardContent>
              </Card>
            )}

            {/* Signature Form */}
            <div className="space-y-4 bg-gradient-to-br from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-200">
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                <PenTool className="h-5 w-5 text-green-600" />
                Informations de Signature
              </h3>
              
              <div className="space-y-4">
                {/* Signer Name */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-gray-600" />
                    Nom du Signataire *
                  </Label>
                  <input
                    type="text"
                    value={approvalData.signerName}
                    onChange={(e) => setApprovalData({ ...approvalData, signerName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Votre nom complet"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ce nom apparaîtra comme signataire officiel du document
                  </p>
                </div>

                {/* Signature Date */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-gray-600" />
                    Date de Signature *
                  </Label>
                  <input
                    type="date"
                    value={approvalData.signatureDate}
                    onChange={(e) => setApprovalData({ ...approvalData, signatureDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Comments */}
                <div>
                  <Label className="mb-2 block">
                    Commentaires (optionnel)
                  </Label>
                  <Textarea
                    value={approvalData.comments}
                    onChange={(e) => setApprovalData({ ...approvalData, comments: e.target.value })}
                    placeholder="Ajoutez des commentaires sur cette approbation..."
                    rows={3}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Confirmation Message */}
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold mb-1">Confirmation de Signature Électronique</p>
                    <p>
                      En signant ce document, vous confirmez avoir examiné et approuvé son contenu. 
                      Cette signature électronique a la même valeur juridique qu'une signature manuscrite.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setApproveDialogOpen(false);
                  setSelectedDocument(null);
                  setPreviewUrl(null);
                  setApprovalData({
                    signerName: '',
                    signatureDate: new Date().toISOString().split('T')[0],
                    comments: ''
                  });
                }}
                className="px-6"
              >
                Annuler
              </Button>
              <Button
                onClick={handleApprove}
                disabled={!approvalData.signerName.trim()}
                className="bg-green-600 hover:bg-green-700 text-white px-6"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Signer et Approuver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter le Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Document : <span className="font-semibold">{selectedDocument?.name || selectedDocument?.filename}</span>
              </p>
              <Label>Raison du rejet *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Expliquez pourquoi ce document est rejeté..."
                rows={4}
                required
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectDialogOpen(false);
                  setSelectedDocument(null);
                  setRejectionReason('');
                }}
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Confirmer le Rejet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default DocumentApprovals;
